import { t } from '@lingui/core/macro';
import { getLocale } from '@luminovo/frontend-utils';
import { Box, FormHelperText, Stack } from '@mui/material';
import * as React from 'react';

import { useNow } from '../../../hooks/useNow';
import { TextFieldProps } from '../../TextField';
import { FieldDatePicker } from './FieldDatePicker';
import { type DatePickerGranularity } from './internal/GranularityTabs';
import { type DateLocale, resolveDateLocale } from './internal/localization';
import { defaultQuickPicksRange, type QuickPickRange } from './internal/quickPicks';
import { QuickPresets } from './internal/QuickPresets';
import { type DateRangeValue } from './internal/validators';

export interface FieldDateRangePickerProps extends Omit<
    TextFieldProps,
    'value' | 'onChange' | 'onBlur' | 'onFocus' | 'type' | 'inputRef' | 'name'
> {
    value: DateRangeValue | null;
    onChange: (value: DateRangeValue) => void;
    /** Fires on every commit, consistent or not; wire it to RHF's `field.onBlur` so out-of-order pairs validate. */
    onCommit?: (value: DateRangeValue) => void;
    minDate?: string;
    maxDate?: string;
    locale?: DateLocale;
    /** Forwarded to the start trigger's input element. */
    inputRef?: React.Ref<HTMLInputElement>;
    /** The inputs get `${name}.from` and `${name}.to`, to stay apart in the DOM and form devtools. */
    name?: string;
    /** Limit which granularities the user can switch to. Defaults to all. */
    granularities?: DatePickerGranularity[];
    /** Placeholder for the start trigger. Defaults to a localized "Start date". */
    startPlaceholder?: string;
    /** Placeholder for the end trigger. Defaults to a localized "End date". */
    endPlaceholder?: string;
    /** Quick-pick chips above the fields, off by default for dense layouts; `'default'` is the standard set. */
    presets?: QuickPickRange[] | 'default';
}

const EMPTY: DateRangeValue = { from: null, to: null };

/** Two linked pickers; a picked period commits its first day on the start side and its last on the end side. */
export const FieldDateRangePicker = React.forwardRef<HTMLDivElement, FieldDateRangePickerProps>(
    function FieldDateRangePicker(props, ref) {
        const {
            value,
            onChange,
            onCommit,
            minDate,
            maxDate,
            locale,
            inputRef,
            disabled,
            size,
            granularities,
            startPlaceholder,
            endPlaceholder,
            name,
            presets,
            error: errorProp,
            helperText,
            ...rest
        } = props;

        const resolvedLocale: DateLocale = locale ?? resolveDateLocale(getLocale());
        const safeValue = value ?? EMPTY;

        const inconsistent = isInconsistent(safeValue);

        // Each inner channel maps to one outer one, so RHF validates every pick, including out-of-order ones.
        const handleFromChange = (next: string | null) => {
            onChange({ from: next, to: safeValue.to });
        };
        const handleToChange = (next: string | null) => {
            onChange({ from: safeValue.from, to: next });
        };
        const handleFromCommit = (next: string | null) => {
            onCommit?.({ from: next, to: safeValue.to });
        };
        const handleToCommit = (next: string | null) => {
            onCommit?.({ from: safeValue.from, to: next });
        };

        // Shown even without a validator, as in uncontrolled use; `createDateRangeValidator` enforces the rule.
        const localError = inconsistent ? t`End date must be on or after start date` : null;
        const isError = Boolean(errorProp) || inconsistent;
        // The consistency hint wins over `helperText`, even before RHF's onBlur fires.
        const message: React.ReactNode = inconsistent ? localError : helperText;

        const now = useNow();
        const resolvedPresets = React.useMemo(() => {
            if (presets === undefined) return [];
            if (presets === 'default') return defaultQuickPicksRange(now);
            return presets;
        }, [presets, now]);

        const handlePresetPick = (id: string) => {
            const found = resolvedPresets.find((p) => p.id === id);
            if (!found) return;
            const next: DateRangeValue = { from: found.from, to: found.to };
            onChange(next);
            onCommit?.(next);
        };

        return (
            <Box ref={ref} sx={{ display: 'inline-flex', flexDirection: 'column', width: '100%' }}>
                {resolvedPresets.length > 0 && (
                    <QuickPresets
                        presets={resolvedPresets.map((p) => ({ id: p.id, label: p.label }))}
                        onPick={handlePresetPick}
                        aria-label={t`Quick range presets`}
                        padding="0 0 8px"
                    />
                )}
                <Stack
                    direction="row"
                    spacing="8px"
                    sx={{
                        alignItems: 'flex-start',
                    }}
                >
                    <FieldDatePicker
                        {...rest}
                        name={name ? `${name}.from` : undefined}
                        value={safeValue.from}
                        onChange={handleFromChange}
                        onCommit={handleFromCommit}
                        minDate={minDate}
                        maxDate={safeValue.to ?? maxDate}
                        locale={resolvedLocale}
                        inputRef={inputRef}
                        disabled={disabled}
                        size={size}
                        granularities={granularities}
                        periodAnchor="start"
                        hideQuickPresets
                        placeholder={startPlaceholder ?? t`Start date`}
                        error={isError}
                        sx={{ flex: 1 }}
                    />
                    <FieldDatePicker
                        {...rest}
                        name={name ? `${name}.to` : undefined}
                        value={safeValue.to}
                        onChange={handleToChange}
                        onCommit={handleToCommit}
                        minDate={safeValue.from ?? minDate}
                        maxDate={maxDate}
                        locale={resolvedLocale}
                        disabled={disabled}
                        size={size}
                        granularities={granularities}
                        periodAnchor="end"
                        hideQuickPresets
                        placeholder={endPlaceholder ?? t`End date`}
                        error={isError}
                        sx={{ flex: 1 }}
                    />
                </Stack>
                {message && (
                    <FormHelperText error={isError} sx={{ marginLeft: '14px' }}>
                        {message}
                    </FormHelperText>
                )}
            </Box>
        );
    },
);

function isInconsistent(value: DateRangeValue): boolean {
    return Boolean(value.from && value.to && value.from > value.to);
}
